/**
 * Production-safe logging utility.
 *
 * `log`, `info` and `debug` print everywhere EXCEPT production; `warn` and
 * `error` always print, because a silent production failure is worse than a
 * noisy one — but
 * S116: they printed their arguments VERBATIM, so a model API key, a student's
 * email, a password inside a rejected-credential object and a student uid all
 * reached a console anybody can reopen. `sentry.ts` has scrubbed exactly these
 * shapes out of Sentry events since it was written, on the stated grounds that
 * this is a children's platform; the console was never held to the same
 * standard, and on a shared classroom Chromebook the console is the device the
 * next child uses. Both paths now share `src/utils/redaction.ts`.
 */

import { redact } from './redaction';

interface Logger {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  debug: (...args: unknown[]) => void;
}

/**
 * Print without letting the console take the caller down.
 *
 * S116: there was no try/catch anywhere in this module, and this repo REPLACES
 * `console.error` (`secureErrorHandler.ts`). Nearly every use here is
 * `catch (err) { logger.error('…', err) }`, so a throwing console turned a
 * HANDLED error into an unhandled one and discarded the original — the
 * component unmounted with a stack naming the logger rather than the bug.
 */
const safely = (write: (...args: unknown[]) => void, args: unknown[]): void => {
  try {
    write(...args);
  } catch {
    // A console that cannot print is not a reason to lose the caller's error.
  }
};

const createLogger = (): Logger => {
  const isDevelopment = process.env.NODE_ENV === 'development';
  /**
   * S145: the diagnostic levels were gated on `=== 'development'`, not
   * `!== 'production'`. `vite.config.ts` defines NODE_ENV as the Vite MODE
   * unless the mode is "production", so an E2E build ships `NODE_ENV === 'e2e'`
   * and a staging build ships `'staging'` — and both produced no diagnostic
   * logging at all while still emitting warn and error: the worst of both, on
   * exactly the two builds someone is watching a console for. Everything outside
   * development is redacted anyway (below), so turning them on there costs
   * nothing and is what S136 did for error reporting for the same reason.
   */
  const isProduction = process.env.NODE_ENV === 'production';
  // Redaction costs a deep copy, and a developer needs the real values in front
  // of them. Outside development the values are the risk, not the debugging aid.
  const prepare = (args: unknown[]): unknown[] =>
    isDevelopment ? args : args.map((arg) => redact(arg));

  return {
    log: (...args: unknown[]) => {
      if (!isProduction) {
        // eslint-disable-next-line no-console -- logger implementation
        safely(console.log, prepare(args));
      }
    },

    info: (...args: unknown[]) => {
      if (!isProduction) {
        // eslint-disable-next-line no-console -- logger implementation
        safely(console.info, prepare(args));
      }
    },

    warn: (...args: unknown[]) => {
      // Always show warnings — redacted outside development.
      safely(console.warn, prepare(args));
    },

    error: (...args: unknown[]) => {
      // Always show errors — redacted outside development.
      safely(console.error, prepare(args));
    },

    debug: (...args: unknown[]) => {
      if (!isProduction) {
        // eslint-disable-next-line no-console -- logger implementation
        safely(console.debug, prepare(args));
      }
    }
  };
};

export const logger = createLogger();

// Helper for component-specific logging
export const createComponentLogger = (componentName: string) => {
  return {
    log: (...args: unknown[]) => logger.log(`[${componentName}]`, ...args),
    info: (...args: unknown[]) => logger.info(`[${componentName}]`, ...args),
    warn: (...args: unknown[]) => logger.warn(`[${componentName}]`, ...args),
    error: (...args: unknown[]) => logger.error(`[${componentName}]`, ...args),
    debug: (...args: unknown[]) => logger.debug(`[${componentName}]`, ...args)
  };
};
